import { TFunction } from "i18next";
import { ApiError } from "src/models";

/** Refusal codes the account-service returns in `error.data.data.code` (BusinessResponse body). */
export const REFUSAL_CODES = [
  "ROSTER_MANAGED",
  "STALE_RECORD",
  "REACTIVATE_INSTEAD",
  "EMAIL_IN_OTHER_DISTRICT",
  "LAST_DISTRICT_ADMIN",
  "ROLE_CEILING",
  "SELF_ACTION",
  "SSO_ACCOUNT",
  "POSSIBLE_DUPLICATE",
  "INVITATION_REVOKED",
  "INVITATION_EXPIRED",
] as const;

export type RefusalCode = (typeof REFUSAL_CODES)[number];

/** The server's message for an RTK Query error, or `fallback` when it sent none. */
export const messageOf = (error: unknown, fallback: string) => {
  const err = error as ApiError | undefined;
  return Array.isArray(err?.data?.message)
    ? err.data.message.join(", ")
    : (err?.data?.message ?? fallback);
};

/** The refusal code of an RTK Query error, when it carries a known one. */
export const refusalCode = (error: unknown): RefusalCode | undefined => {
  const code = (error as ApiError | undefined)?.data?.data?.code;
  return (REFUSAL_CODES as readonly string[]).includes(code ?? "")
    ? (code as RefusalCode)
    : undefined;
};

/** Plain-language copy for a refusal code (contracts/people-console.md §Copy). */
export const refusalCopy = (t: TFunction, code: RefusalCode): string =>
  t(`errors.refusal.${code}`);

/** Codes that can carry copy under `errors.<CODE>`: upper snake case only, so no other key is reachable. */
const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

/**
 * The `code` an RTK Query error body carries: `{ data: { code } }` (BusinessResponse refusals) or a
 * top-level `{ code, message }` (contracts/district-admin-api.md, "Refusals").
 */
export const errorCodeOf = (error: unknown): string | undefined => {
  const data = (error as { data?: unknown } | undefined)?.data as
    | { code?: unknown; data?: { code?: unknown } }
    | undefined;
  const code = data?.data?.code ?? data?.code;
  return typeof code === "string" && CODE_PATTERN.test(code) ? code : undefined;
};

/**
 * Plain-language copy for an error code: `errors.<CODE>` (district refusals and the data-request and
 * sync `errorCode` values), then `errors.refusal.<CODE>` (staff refusals). Undefined when it has none.
 */
export const errorCodeCopy = (
  t: TFunction,
  code: string | null | undefined,
): string | undefined => {
  if (!code || !CODE_PATTERN.test(code)) return undefined;
  return (
    t(`errors.${code}`, { defaultValue: "" }) ||
    t(`errors.refusal.${code}`, { defaultValue: "" }) ||
    undefined
  );
};

/** The copy for an RTK Query error's `{ code }` when it has some, otherwise the server's message or `fallback`. */
export const errorMessage = (
  t: TFunction,
  error: unknown,
  fallback: string,
): string => errorCodeCopy(t, errorCodeOf(error)) ?? messageOf(error, fallback);
